import { Box, Button, Divider, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import FormWrapper from "../common/FormWrapper";
import FormField from "../common/FormField";
import CloseIcon from "@mui/icons-material/Close";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useMemo, useState } from "react";
import {
  fetchSections,
  pusblishTemplate,
  sectionAutoSave,
  setFormData,
  submitSection,
} from "../../slices/FbSections";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import { SectionSchema } from "../../utils/schemas/initial";
import { notify } from "../../../../bmr/utils/methods/notification";
import { getOptions } from "../../slices/FbProperty";
import DatePickerField from "../../../../bmr/components/common/DatePicker";
import { evaluate, round } from "mathjs";
import DeleteModal from "../../../../bmr/components/common/BaseModal/DeleteModal";
import {
  fetchTemplates,
  updateTemplateStatus,
} from "../../../../bmr/slices/formTemplate";

const DynamicFormGenerator = ({
  formJson,
  onSubmit,
  loading,
  formData,
  setDFormData,
}) => {
  const dispatch = useDispatch();
  const fieldOptions = useSelector((state) => state.bmrFbProperty?.options);
  const fieldTypeMap = {
    General: "text",
    Note: "textarea",
    Validations: "text",
    Label: "text",
    Toggle: "switch",
    "Input Radio": "radio-group",
    "Input Dropdown": "select",
    "Input Text": "text",
    "Input Number": "number",
    "Static Number": "number",
    "Input Email": "email",
    "Input Current Time": "time",
    "Input Date": "date",
    "Input Date and Time": "datetime",
    "Linked Field": "linked-field",
    Hyperlink: "url",
    "File Upload": "file",
  };

  const layoutOptions = {
    12: { gridSize: 12, cols: 1 },
    6: { gridSize: 6, cols: 2 },
    4: { gridSize: 4, cols: 3 },
    3: { gridSize: 3, cols: 4 },
  };

  const processSections = (sections) => {
    return sections.map((section) => {
      const sortedFields = [...section.fields].sort(
        (a, b) => a.order - b.order
      );

      const fieldGroups = [];
      let currentGroup = [];
      let currentLayout = null;

      sortedFields.forEach((field) => {
        const fieldLayout = field.property.field_layout || 12;

        if (currentLayout !== fieldLayout) {
          if (currentGroup.length > 0) {
            fieldGroups.push({
              layout: currentLayout,
              fields: currentGroup,
            });
          }
          currentGroup = [];
          currentLayout = fieldLayout;
        }

        currentGroup.push({
          ...field,
          type: fieldTypeMap[field.property.field_type] || "text",
          required: field.property.general?.required || false,
        });
      });

      if (currentGroup.length > 0) {
        fieldGroups.push({
          layout: currentLayout,
          fields: currentGroup,
        });
      }

      return {
        ...section,
        fieldGroups,
      };
    });
  };

  const formSections = processSections(formJson);
  useEffect(() => {
    const fetchFieldOptions = () => {
      formJson.forEach((section) => {
        section.fields.forEach((field) => {
          const fieldType = field.property.field_type;
          if (fieldType === "Input Radio" || fieldType === "Input Dropdown") {
            dispatch(getOptions({ fieldId: field.input_uuid }));
          }
        });
      });
    };

    if (formJson?.length) {
      fetchFieldOptions();
    }
  }, [formJson, dispatch]);

  const getFieldOptions = (fieldUuid) => {
    const fieldData = fieldOptions?.[fieldUuid];
    if (!fieldData) return [];

    return fieldData.options.map((opt) => ({
      value: opt.option_uuid,
      label: opt.label,
    }));
  };

  const handleFieldChange = (fieldUuid, value) => {
    setDFormData((prev) => {
      const updatedSections = prev.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) =>
          field.input_uuid === fieldUuid ? { ...field, value } : field
        ),
      }));

      return { ...prev, sections: updatedSections };
    });
  };

  const handleDateChange = (fieldUuid, dateValue) => {
    handleFieldChange(fieldUuid, dateValue);
  };

  const parseTimeValue = (val) => {
    if (!val || !val.time_format) return null;
    const d = new Date(val.time_format);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatTimeValue = (date) => {
    if (!date) return { time_format: "", time_display_format: "" };

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    const time_display_format = `${day}/${month}/${year} ${hours}:${minutes}`;
    return {
      time_format: date.toISOString(),
      time_display_format: time_display_format,
    };
  };

  const getHiddenFields = () => {
    const hidden = [];

    if (!formData?.sections?.length) return hidden;
    const allFields = formData.sections.flatMap((s) => s.fields || []);
    const fieldsWithConditions = allFields.filter(
      (f) => f.property?.conditions?.length
    );
    fieldsWithConditions.forEach((fieldItem) => {
      const selectedValue = allFields.find(
        (f) => f.input_uuid === fieldItem.input_uuid
      )?.value;
      fieldItem.property.conditions.forEach((cond) => {
        if (cond.fbt_option_uuid !== selectedValue) {
          hidden.push(cond.target_input_field_uuid);
        }
      });
    });

    return hidden;
  };

  const hiddenFields = getHiddenFields();

  const allOptionsDict = useMemo(() => {
    return Object.values(fieldOptions || {}).reduce((acc, field) => {
      field?.options?.forEach((opt) => {
        acc[opt.option_uuid] = opt.label;
      });
      return acc;
    }, {});
  }, [fieldOptions]);

  return (
    <FormWrapper onSubmit={onSubmit} loading={loading} className="p-3 ">
      {formSections.map((section, index) => (
        <Box key={section?.section_uuid} >
          <Accordion
            defaultExpanded
            elevation={0}
            sx={{
              border: "none",
              margin: "0px",
              "&:before": { display: "none" },
            }}
            // className="p-2"
          >
            {(section?.property?.display_details ||
              section?.property?.display_field_number) && (
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className="border-bottom m-0 px-2 expanded-accordian p-[0.5rem]"
                sx={{
                  "& .MuiAccordionSummary-content": {
                    margin: 0,
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "600" }}>
                  {section?.property?.display_details &&
                  section?.property?.display_field_number ? (
                    <>
                      {`${index + 1}. ${section?.name}`}
                      {section?.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            ml: 1,
                            fontWeight: 400,
                            color: "text.secondary",
                          }}
                        >
                          {section?.description}
                        </Typography>
                      )}
                    </>
                  ) : section?.property?.display_field_number ? (
                    `${index + 1}.`
                  ) : section?.property?.display_details ? (
                    <>
                      {section?.name}
                      {section?.description && (
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 400, color: "text.secondary" }}
                        >
                          {section?.description}
                        </Typography>
                      )}
                    </>
                  ) : null}
                </Typography>
              </AccordionSummary>
            )}

            <AccordionDetails className="p-0 px-2">
              {section.fieldGroups.map((group, groupIndex) => (
                <Grid key={groupIndex} container>
                  {group.fields.map((field) => {
                    if (hiddenFields.includes(field.input_uuid)) {
                      return null;
                    }
                    const fieldValue =
                      formData.sections
                        .flatMap((s) => s.fields)
                        .find((f) => f.input_uuid === field.input_uuid)
                        ?.value ||
                      field.value ||
                      "";

                    const showFormula = field?.property?.calculation
                      ?.display_formula
                      ? JSON.parse(
                          field?.property?.calculation?.display_formula
                        )
                      : false;
                    const calulationProp = field?.property?.calculation;

                    return (
                      <Grid
                        item
                        key={field.input_uuid}
                        xs={12}
                        sm={layoutOptions[group.layout]?.gridSize || 12}
                      >
                        <Box sx={{ paddingTop: "1rem" }}>
                          <Typography
                            variant="subtitle1"
                            className="inline-block font-normal"
                            sx={{ paddingBottom: "0.625rem" }}
                          >
                            {field?.label}{" "}
                            {field?.property?.general?.required && (
                              <Box
                                component="span"
                                className="text-red-500"
                                sx={{ ml: 0.5 }}
                              >
                                *
                              </Box>
                            )}
                          </Typography>
                          <Typography variant="subtitle1">
                            {calulationProp?.unit_of_measurement
                              ? `(${calulationProp?.unit_of_measurement})`
                              : ""}
                          </Typography>
                          {field.type === "url" ? (
                            <Typography component="div">
                              <a
                                href={field.property.hyperlink?.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {field.property.hyperlink?.name || field.label}
                              </a>
                            </Typography>
                          ) : field.type === "file" ||
                            field.property.field_type === "Barcode" ? (
                            <Box>
                              {fieldValue ? (
                                <Box>
                                  <Typography variant="body2">
                                    {fieldValue.name || "Uploaded File"}
                                  </Typography>
                                  {fieldValue?.type?.startsWith("image/") && (
                                    <img
                                      src={URL.createObjectURL(fieldValue)}
                                      alt="Preview"
                                      style={{
                                        width: "200px",
                                        borderRadius: "8px",
                                        marginTop: "8px",
                                      }}
                                    />
                                  )}

                                  {fieldValue?.type === "application/pdf" && (
                                    <iframe
                                      src={URL.createObjectURL(fieldValue)}
                                      title="PDF Preview"
                                      width="100%"
                                      height="400px"
                                      style={{
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        marginTop: "8px",
                                      }}
                                    />
                                  )}
                                </Box>
                              ) : (
                                <FormField
                                  name={field.input_uuid}
                                  type="file"
                                  label={field?.name}
                                  required={field.required}
                                  fullWidth
                                  onChange={(e) =>
                                    handleFieldChange(
                                      field.input_uuid,
                                      e.target.files[0]
                                    )
                                  }
                                />
                              )}
                            </Box>
                          ) : field.property.field_type === "Calculation" ? (
                            <>
                              <FormField
                                name={field.input_uuid}
                                type="number"
                                label=""
                                fullWidth
                                value={fieldValue}
                                disabled
                                size="small"
                              />
                              {showFormula && calulationProp?.formula && (
                                <Typography variant="caption">
                                  Formula: {field?.parsedFormula}
                                </Typography>
                              )}
                            </>
                          ) : field.property.field_type === "Linked Field" ? (
                            <>
                              <FormField
                                name={field.input_uuid}
                                label=""
                                fullWidth
                                value={allOptionsDict[fieldValue] || fieldValue}
                                disabled
                                size="small"
                              />
                            </>
                          ) : field.property.field_type === "Input Date" ? (
                            // Date picker for Input Date
                            <Box className="preview-date-picker">
                              <DatePickerField
                                format="DD/MM/YYYY"
                                value={fieldValue}
                                handleChange={(date) =>
                                  handleDateChange(field.input_uuid, date)
                                }
                                className="m-0"
                                disabled
                              />
                            </Box>
                          ) : field.property.field_type ===
                            "Input Current Time" ? (
                            // Time input for Current Time
                            <Box className="preview-date-picker">
                              <DatePickerField
                                selected={parseTimeValue(fieldValue)}
                                onChange={(date) =>
                                  handleDateChange(
                                    field.input_uuid,
                                    formatTimeValue(date)
                                  )
                                }
                                timeInputLabel="Time:"
                                dateFormat="MM/dd/yyyy h:mm aa"
                                showTimeInput
                                disabled
                              />
                            </Box>
                          ) : (
                            <FormField
                              name={field.input_uuid}
                              type={field.type}
                              label=""
                              placeholder={
                                field.placeholder ||
                                `Enter ${field.label?.toLowerCase() || "value"}`
                              }
                              required={field.required}
                              fullWidth
                              value={fieldValue}
                              onChange={(e) => {
                                handleFieldChange(
                                  field.input_uuid,
                                  e.target.value
                                );
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "0.5rem",
                                  backgroundColor: "#f9f9f9",
                                },
                                "& .MuiInputBase-input": {
                                  padding: "0.75rem 0.875rem",
                                },
                              }}
                              {...(field.type === "select" && {
                                options: getFieldOptions(field.input_uuid) || [
                                  {
                                    value: "",
                                    label: `Select ${
                                      field.label?.toLowerCase() || ""
                                    }`,
                                  },
                                ],
                                defaultValue: fieldValue || "",
                              })}
                              // {...(field.type === "radio-group" && { //@info remove comment later
                              {
                              ...(
                                (field.property.field_type === "Input Radio" ||
                                  field.type === "radio-group") && {
                                  options: getFieldOptions(field.input_uuid) || [],
                                  row: true,
                                  size: "small",
                                  value: fieldValue || "",
                                }
                              )
                              }
                            />
                          )}
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{ mb: 1, color: "text.secondary" }}
                        >
                          {field?.property?.note?.message_note
                            ? `Note : ${field?.property?.note?.message_note}`
                            : ""}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>
      ))}
    </FormWrapper>
  );
};

const DynamicPreview = ({ handleCloseDrawer, cardId, getValues }) => {
  const { id } = useParams();
  const location = useLocation();
  const [loading,setLoading] = useState(false)
  
  const FORM_SCHEMA_UPDATED = useSelector(
    (state) => state.bmrFbSections.formData.sections
  );

  const { fbTemplate } = useSelector((state) => state.bmrFbl);
  let templateName = fbTemplate.items.find(
    (name) => name?.id == (id ?? cardId)
  );

  const dispatch = useDispatch();
  const [formData, setDynamicFormData] = useState({
    sections: FORM_SCHEMA_UPDATED || [],
  });
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
  };

  const handleSubmit = () => {
    dispatch(submitSection(id ?? cardId));
  };

  const handlePublish = async () => {
    try {
      const response = await dispatch(
        pusblishTemplate(id ? id : cardId)
      ).unwrap();
      if (!location.pathname.includes("preview")) {
        handleCloseDrawer(); //@info close the drawer if user is in template list page
      }
      dispatch(updateTemplateStatus({ id, status: response.data.status }));
      dispatch(fetchTemplates());
    } catch (error) {
      notify.error(error?.message || error?.data?.message);
    }
  };

  // Update local state when Redux state changes
  useEffect(() => {
    if (FORM_SCHEMA_UPDATED) {
      setDynamicFormData({ sections: FORM_SCHEMA_UPDATED });
    }
  }, [FORM_SCHEMA_UPDATED]);

  /*The below code block can be removed when autosave starts working fine or 
    we provide a button to save manually before previewing */
  /* Code block start */
  useEffect(() => {
    fetchSetFormData();
  }, []);

  const fetchSetFormData = async () => {
    try {
      setLoading(true)
      if (!location.pathname.includes("form-management")) {
        const currentFormData = getValues('sections');
        await dispatch(sectionAutoSave({ data: {sections:currentFormData}, id }));
      }
      const response = await dispatch(fetchSections(id? id: cardId)).unwrap();

      const modifiedSections =
        response.data.sections.length > 0
          ? injectDefaultValues(response.data.sections)
          : [SectionSchema];
      dispatch(setFormData({ sections: modifiedSections }));
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };
  /* Code block ends */

  const getParsedFormula = (formVariables, formula) => {
    let parsedFormula = formula;
    Object.entries(formVariables).forEach(([key, value]) => {
      const regex = new RegExp(`\\b${key}\\b`, "g");
      parsedFormula = parsedFormula.replace(regex, value);
    });
    return parsedFormula;
  };

  const injectDefaultValues = (sections) => {
    return sections.map((section) => ({
      ...section,
      fields: section.fields.map((field) => {
        const hasValue =
          field.value !== undefined &&
          field.value !== null &&
          field.value !== "";
        const defaultValue = field?.property?.linked_field?.default_val;

        return {
          ...field,
          value: hasValue ? field.value : defaultValue ?? "",
        };
      }),
    }));
  };

  useEffect(() => {
    if (!formData?.sections?.length) return;

    const inputValues = {};
    const fieldInputLabels = {};
    const updatedFields = [];

    for (const section of formData.sections) {
      for (const field of section.fields || []) {
        const { input_uuid, value, property } = field;
        const { field_type } = property || {};
        fieldInputLabels[input_uuid] = field.label;
        inputValues[input_uuid] =
          field_type === "Input Number" && value
            ? parseFloat(value) ?? 0
            : value;
      }
    }

    for (const section of formData.sections) {
      for (const field of section.fields || []) {
        const { input_uuid, value: currentValue, property } = field;
        const {
          field_type,
          calculation,
          input_calculation_variables,
          linked_field,
        } = property || {};

        if (field_type === "Calculation" && calculation?.formula) {
          const variables = {};
          const formVariables = {};

          for (const variable of input_calculation_variables || []) {
            formVariables[variable.name] =
              fieldInputLabels[variable.linked_field_uuid];
            variables[variable.name] =
              inputValues[variable.linked_field_uuid] ||
              (!isNaN(variable.default_val) &&
                parseFloat(variable.default_val)) ||
              0;
          }

          try {
            let result = round(
              evaluate(calculation.formula, variables),
              calculation.decimal_places || 4
            );
            if (calculation.decimal_places != null) {
              result = parseFloat(
                Number(result).toFixed(calculation.decimal_places)
              );
            }
            const resultStr = result?.toString() ?? "";

            if (currentValue !== resultStr) {
              updatedFields.push({
                fieldUuid: input_uuid,
                value: resultStr,
                parsedFormula: getParsedFormula(
                  formVariables,
                  calculation.formula
                ),
              });
            }
          } catch (err) {
            console.warn(
              `Invalid formula for field "${input_uuid}": ${err.message}`
            );
          }
        }

        if (field_type === "Linked Field" && linked_field?.linked_field) {
          const newValue = inputValues[linked_field.linked_field] || "";
          if (currentValue !== newValue) {
            updatedFields.push({
              fieldUuid: input_uuid,
              value: newValue || property.linked_field.default_val,
            });
          }
        }
      }
    }

    if (!updatedFields.length) return;

    setDynamicFormData((prev) => {
      const updatesMap = Object.fromEntries(
        updatedFields.map(({ fieldUuid, value, parsedFormula }) => [
          fieldUuid,
          { value, parsedFormula },
        ])
      );

      const newSections = prev.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          const newValue = updatesMap[field.input_uuid]?.value;
          if (newValue !== undefined && field.value !== newValue) {
            return {
              ...field,
              value: newValue,
              parsedFormula: updatesMap[field.input_uuid]?.parsedFormula,
            };
          }
          return field;
        }),
      }));

      const isSame =
        JSON.stringify(newSections) === JSON.stringify(prev.sections);
      if (isSame) return prev;

      return {
        ...prev,
        sections: newSections,
      };
    });
  }, [formData]);

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#E8EFFC",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
         Template Name - {templateName?.name}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="contained" onClick={handlePublish}sx={{borderRadius:'0.375rem !important', width:"7.18rem !important "}}>
            Publish
          </Button>
          <IconButton onClick={handleCloseDrawer} sx={{color:'black'}}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      {loading ? (
        <Box className="p-3">
          <Skeleton variant="text" width="30%" height={20} />
          <Divider />
          <Skeleton variant="text" width="100%" height={60} />
          <Divider />
          <Skeleton variant="text" width="100%" height={60} />
        </Box>
      ) : (
        <Box className="p-2">
        <DynamicFormGenerator
          formJson={formData.sections}
          formData={formData}
          setDFormData={setDynamicFormData}
          loading={false}
          />
          </Box>
      )}

      <DeleteModal
        open={openConfirmation}
        setClose={handleCloseConfirmation}
        title="Template is not submitted yet, would you like to submit"
        buttonText="Submit"
        handleSuccess={handleSubmit}
        handleRejection={handleCloseConfirmation}
        rejectButtonText="Cancel"
      />
    </div>
  );
};

export default DynamicPreview;
